import React from "react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import NetworkOverlay from "../HeroSection/NetworkOverlay";
import {
  FaNetworkWired,
  FaHeadset,
  FaBolt,
  FaSnowflake,
  FaServer,
  FaArrowRight,
} from "react-icons/fa";
import HomeDataCenters from "../HomeDetailSection/HomeDataCenters";
import { ALL_DATA_CENTERS } from "../../constant/AllDataCenters";
import { COMPANY_DATA } from "../../constant/CompanyData";
import dataCenterHero from "../../assets/Services/dataCenter.webp";
import Badge from "../common/Badge";
import Eyebrow from "../common/Eyebrow";
import InfraShowcase3D from "../DataCenters/InfraShowcase3D";
import ComparisonTable from "../DataCenters/ComparisonTable";
import CertTrustStrip from "../DataCenters/CertTrustStrip";
import DataCenterFAQ, {
  DATA_CENTER_FAQS,
} from "../DataCenters/DataCenterFAQ";
import CustomerStory from "../company/Testimonials";
import TiltCard, {
  cardsContainerVariants,
} from "../DataCenters/TiltCard";

const HIGHLIGHTS = [
  {
    icon: FaNetworkWired,
    title: "Carrier-Neutral Connectivity",
    description:
      "Multi-ISP peering and dark fiber access for low-latency, high-bandwidth connections.",
  },
  {
    icon: FaHeadset,
    title: "24/7 NOC Monitoring",
    description:
      "Round-the-clock monitoring, rapid incident response, and on-site technical assistance.",
  },
  {
    icon: FaBolt,
    title: "Redundant Power",
    description:
      "N+1 power redundancy with generator-backed UPS systems to eliminate single points of failure.",
  },
  {
    icon: FaSnowflake,
    title: "Precision Cooling",
    description:
      "Advanced environmental controls including dry coolers, CRAC systems, and continuous temperature monitoring.",
  },
  {
    icon: FaServer,
    title: "Hands-On Remote Management",
    description:
      "Full rack and cage provisioning with remote-hands support whenever you need it.",
  },
];

const DataCenters = () => {
  const navigate = useNavigate();
  const cityKeys = Object.keys(ALL_DATA_CENTERS);

  // Enhanced Schema markup for Organization and LocalBusiness
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "QuantaRock Systems",
    url: "https://quantarock.in",
    logo: "https://quantarock.in/logo.png",
    description:
      "Enterprise data center solutions provider across India and international markets",
    sameAs: [
      "https://www.facebook.com/profile.php?id=61550422080802",
      "https://x.com/QuantaRock",
      "https://www.linkedin.com/company/quantarock",
      "https://www.youtube.com/@Quantarock",
    ],
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "Customer Support",
      telephone: COMPANY_DATA.phoneNumber,
      email: "support@quantarock.in",
    },
  };

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": "https://quantarock.in/data-centers",
    name: "QuantaRock Systems - Data Centers India & International",
    description:
      "Premium data center facilities offering colocation, dedicated servers, and cloud hosting across India and international markets, including Dubai, UAE",
    url: "https://quantarock.in/data-centers",
    telephone: COMPANY_DATA.phoneNumber,
    email: "sales@quantarock.in",
    areaServed: [
      { "@type": "City", name: "Indore", addressCountry: "IN" },
      { "@type": "City", name: "Bhopal", addressCountry: "IN" },
      { "@type": "City", name: "Pune", addressCountry: "IN" },
      { "@type": "City", name: "Nashik", addressCountry: "IN" },
      { "@type": "City", name: "Mumbai", addressCountry: "IN" },
      { "@type": "City", name: "Bangalore", addressCountry: "IN" },
      { "@type": "City", name: "Mysore", addressCountry: "IN" },
      { "@type": "City", name: "Chennai", addressCountry: "IN" },
      { "@type": "City", name: "Kolkata", addressCountry: "IN" },
      { "@type": "City", name: "Patna", addressCountry: "IN" },
      { "@type": "City", name: "Guwahati", addressCountry: "IN" },
      { "@type": "City", name: "Delhi", addressCountry: "IN" },
      { "@type": "City", name: "Noida", addressCountry: "IN" },
      { "@type": "City", name: "Chandigarh", addressCountry: "IN" },
      { "@type": "City", name: "Jaipur", addressCountry: "IN" },
      { "@type": "City", name: "Raipur", addressCountry: "IN" },
      { "@type": "City", name: "Ahmedabad", addressCountry: "IN" },
      { "@type": "City", name: "Surat", addressCountry: "IN" },
      { "@type": "City", name: "Hyderabad", addressCountry: "IN" },
      { "@type": "City", name: "Dubai", addressCountry: "AE" },
    ],
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Data center services",
      itemListElement: [
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "Colocation Services",
            description:
              "Secure rack space and cabinet colocation with 99.99% uptime",
            url: "https://quantarock.in/services/colocation_services",
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "Dedicated Servers",
            description: "High-performance dedicated server hosting",
            url: "https://quantarock.in/services/dedicated_servers",
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "Cloud Hosting",
            description: "Scalable cloud hosting solutions",
            url: "https://quantarock.in/services/cloud_services",
          },
        },
        {
          // No dedicated DR page yet — left without a url rather than
          // pointing at something that doesn't exist.
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "Disaster Recovery",
            description:
              "Comprehensive disaster recovery and business continuity services",
          },
        },
      ],
    },
    priceRange: "₹₹₹",
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: DATA_CENTER_FAQS.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://quantarock.in/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Data Centers",
        item: "https://quantarock.in/data-centers",
      },
    ],
  };

  return (
    <>
      <Helmet>
        <title>
          QuantaRock Data Centers | India &amp; International Colocation,
          Hosting &amp; Cloud
        </title>
        <meta
          name="description"
          content="QuantaRock Systems offers premium data center solutions across India and international markets - secure colocation, dedicated servers, cloud hosting, and disaster recovery with 99.99% uptime, advanced security, and 24/7 expert support in 19 Indian cities including Indore, Pune, Bangalore, Mumbai, Delhi, Hyderabad, Kolkata, Chennai, Jaipur, Ahmedabad, Chandigarh, Noida, Bhopal, Raipur, Surat, Nashik, Patna, Mysore, and Guwahati, plus an international location in Dubai, UAE."
        />
        <meta
          name="keywords"
          content="quantarock data centers, data center quantarock, quantarock colocation, quantarock hosting, data centers India, colocation services India, dedicated servers India, cloud hosting India, tier 3 data center, secure data center, enterprise data center solutions, disaster recovery, managed hosting, India data center provider, international data center, dubai data center, data center UAE, QuantaRock Systems, data center Indore Pune Bangalore Mumbai Delhi Hyderabad Dubai"
        />
        <meta
          property="og:title"
          content="QuantaRock Data Centers | Premium Colocation & Hosting, India & International"
        />
        <meta
          property="og:description"
          content="QuantaRock Systems provides enterprise-grade data centers across India and international markets, including Dubai, UAE, with secure colocation, dedicated servers, cloud hosting, and disaster recovery. Industry-leading uptime, security, and 24/7 support."
        />
        <meta property="og:type" content="business.business" />
        <meta property="og:url" content="https://quantarock.in/data-centers" />
        <meta
          property="og:image"
          content="https://quantarock.in/og-image.jpg"
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="QuantaRock Data Centers | Premium Colocation & Hosting, India & International"
        />
        <meta
          name="twitter:description"
          content="Enterprise-grade data centers across India and international markets, including Dubai, UAE, with secure colocation, dedicated servers, cloud hosting, and disaster recovery."
        />
        <meta
          name="twitter:image"
          content="https://quantarock.in/og-image.jpg"
        />
        {/* Must stay a literal hex — the browser UA reads this before CSS,
            so var(--website-primary-color) would not resolve. Keep in sync
            with --website-primary-color in src/variables.css. */}
        <meta name="theme-color" content="#192440" />
        <link rel="canonical" href="https://quantarock.in/data-centers" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [organizationSchema, localBusinessSchema],
          })}
        </script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>

     {/* Hero */}
<section className="relative w-full overflow-hidden bg-[var(--surface-dark)] py-20 md:py-28">
  {/* Background Image */}
  <img
    src={dataCenterHero}
    alt="QuantaRock data center server infrastructure"
    className="absolute inset-0 h-full w-full object-cover opacity-70"
  />

  {/* Dark Overlay */}
  <div className="absolute inset-0 bg-gradient-to-r from-[rgba(var(--surface-dark-rgb),0.75)] via-[rgba(var(--surface-dark-rgb),0.55)] to-[rgba(var(--surface-dark-rgb),0.35)]" />

  <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/55" />

  {/* Network Connectivity Background */}
  <div
    className="pointer-events-none absolute inset-0 z-[1] opacity-30"
    aria-hidden="true"
  >
    <NetworkOverlay />
  </div>

  {/* Hero Content */}
  <div className="relative z-10 mx-auto max-w-4xl px-6 text-center">
    <Badge>India &amp; International Data Center Network</Badge>

    <h1
      style={{
        textShadow:
          "0 0 1px rgba(0,0,0,1), 0 0 3px rgba(0,0,0,1), 0 1px 4px rgba(0,0,0,0.95), 0 2px 10px rgba(0,0,0,0.9), 0 6px 20px rgba(0,0,0,0.7)",
      }}
      className="text-4xl font-bold leading-[1.1] text-white sm:text-5xl md:text-6xl"
    >
      QuantaRock Data Center Network
    </h1>

    <p
      style={{
        textShadow:
          "0 0 1px rgba(0,0,0,1), 0 0 2px rgba(0,0,0,1), 0 1px 3px rgba(0,0,0,0.9), 0 4px 12px rgba(0,0,0,0.75)",
      }}
      className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-slate-200 sm:text-lg"
    >
      Discover and compare premium colocation and hosting facilities
      across 19 Indian cities and growing international markets like
      Dubai, built with Tier-3 design, advanced security,
      carrier-neutral connectivity, and scalable infrastructure for
      enterprise-grade workloads.
    </p>

    <div className="mt-8 flex flex-col justify-center gap-4 sm:flex-row">
      <button
        onClick={() => navigate("/contact")}
        className="inline-flex items-center justify-center rounded-full bg-[var(--website-secondary-color)] px-8 py-4 text-sm font-semibold text-[var(--on-secondary)] shadow-lg shadow-[rgba(var(--website-secondary-rgb),0.2)] transition hover:scale-[1.02] hover:brightness-110"
      >
        Request a quote
      </button>

      <a
        href="#cities"
        className="inline-flex items-center justify-center rounded-full border border-white/30 bg-white/10 px-8 py-4 text-sm font-semibold text-white transition hover:bg-white/20"
      >
        Compare cities
      </a>
    </div>
  </div>
</section>
      {/* Live 3D infrastructure showcase + key stats */}
      <InfraShowcase3D />

      {/* Infrastructure highlights */}
      <section className="bg-white px-6 py-20">
        <div className="mx-auto max-w-6xl">
          <div className="text-center">
            <Eyebrow>Infrastructure</Eyebrow>
            <h2 className="mx-auto mt-3 max-w-3xl text-center text-3xl font-bold tracking-tight text-[var(--website-primary-color)] md:text-4xl">
              Built for reliability at every layer
            </h2>
            <p className="mx-auto mt-4 max-w-3xl text-center leading-relaxed text-slate-600">
              Every QuantaRock location is engineered around the same core
              standards for power, connectivity, and security.
            </p>
          </div>

          <motion.div
            variants={cardsContainerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.15 }}
            className="mt-10 grid gap-6 [perspective:800px] sm:grid-cols-2 lg:grid-cols-3"
          >
            {HIGHLIGHTS.map((item) => {
              const Icon = item.icon;
              return (
                <TiltCard
                  key={item.title}
                  className="group rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition-colors duration-300 hover:border-[rgba(var(--website-secondary-rgb),0.6)] hover:shadow-lg"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[var(--website-primary-color)] text-[var(--website-secondary-color)] transition group-hover:bg-[var(--website-secondary-color)] group-hover:text-[var(--website-primary-color)]">
                    <Icon size={20} />
                  </div>
                  <h3 className="mt-5 text-lg font-semibold text-[var(--website-primary-color)]">
                    {item.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">
                    {item.description}
                  </p>
                </TiltCard>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Locations map */}
      <section className="bg-slate-50 px-4 py-16 sm:px-8 md:px-12">
        <div className="max-w-5xl mx-auto">
          <HomeDataCenters />
        </div>
      </section>

      {/* Hosting model comparison */}
      <ComparisonTable />

      {/* Certifications trust strip */}
      <CertTrustStrip />

      {/* Customer testimonials */}
      <CustomerStory />

      {/* FAQ */}
      <DataCenterFAQ />

      {/* City grid */}
      <section id="cities" className="bg-white px-6 py-20">
        <div className="mx-auto max-w-6xl">
          <div className="text-center">
            <Eyebrow>Locations</Eyebrow>
          </div>
          <h2 className="mx-auto mt-3 max-w-3xl text-center text-3xl font-bold tracking-tight text-[var(--website-primary-color)] md:text-4xl">
            Compare available cities
          </h2>
          <p className="mx-auto mt-4 max-w-3xl text-center leading-relaxed text-slate-600">
            Explore local capabilities, services, and support in each of our{" "}
            {cityKeys.length} markets across India and international
            locations.
          </p>

          <div className="mt-10 grid gap-6 [perspective:800px] sm:grid-cols-2 lg:grid-cols-3">
            {cityKeys.map((key) => {
              const cityData = ALL_DATA_CENTERS[key];
              return (
                <TiltCard
                  key={key}
                  className="group flex flex-col rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition-colors duration-300 hover:border-[rgba(var(--website-secondary-rgb),0.6)] hover:shadow-lg"
                >
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="text-xl font-semibold text-[var(--website-primary-color)]">
                      {cityData.city}
                    </h3>
                    <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                      {cityData.state}
                    </span>
                  </div>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">
                    {cityData.intro}
                  </p>
                  <ul className="mt-4 space-y-1.5 text-sm text-slate-600">
                    {cityData.services.slice(0, 4).map((service) => (
                      <li key={service} className="flex items-center gap-2">
                        <span className="h-1 w-1 rounded-full bg-[var(--website-secondary-color)]" />
                        {service}
                      </li>
                    ))}
                  </ul>
                  <a
                    href={`/data-centers/${key}`}
                    className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[var(--website-primary-color)] transition group-hover:gap-3 group-hover:text-[var(--website-secondary-color)]"
                  >
                    Explore {cityData.city} data center
                    <FaArrowRight size={12} />
                  </a>
                </TiltCard>
              );
            })}
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="bg-[var(--surface-dark)] px-6 py-16">
        <div className="mx-auto flex max-w-4xl flex-col items-center gap-6 text-center">
          <h2 className="text-2xl font-bold text-white md:text-3xl">
            Ready to deploy across India or internationally?
          </h2>
          <p className="max-w-xl text-slate-300">
            Tell us your requirements and our team will put together a
            custom quote for your target city or country.
          </p>
          <button
            onClick={() => navigate("/contact")}
            className="inline-flex items-center justify-center rounded-full bg-[var(--website-secondary-color)] px-8 py-4 text-sm font-semibold text-[var(--on-secondary)] shadow-lg shadow-[rgba(var(--website-secondary-rgb),0.2)] transition hover:scale-[1.02] hover:brightness-110"
          >
            Request a quote
          </button>
        </div>
      </section>
    </>
  );
};

export default DataCenters;
